/**
 * 甘特图类型定义
 * 
 * 定义甘特图组件所需的所有类型接口
 */

import type { IssueStatus, IssuePriority, IssueType , MilestoneStatus , DeveloperGroup } from '@/hooks'

// ==================== 工作流类型 ====================

/**
 * 工作流阶段
 */
export type WorkflowStage =
  | 'created'
  | 'product_discussion'
  | 'ui_design'
  | 'ui_confirmation'
  | 'frontend_development'
  | 'backend_development'
  | 'code_review'
  | 'ops_deployment'
  | 'testing'
  | 'product_acceptance'
  | 'done'

/**
 * 阶段时间段
 */
export interface StageTimeRange {
  /** 阶段 */
  stage: WorkflowStage
  /** 开始日期 */
  startDate: string
  /** 结束日期 */
  endDate: string
  /** 预估工时 */
  estimatedHours: number
}

// ==================== 基础类型 ====================

/**
 * 缩放级别
 * - day: 日视图，每天一列
 * - week: 周视图，每周一组
 * - month: 月视图，每月一组
 */
export type ZoomLevel = 'day' | 'week' | 'month'

/**
 * 分组选项
 * - none: 不分组
 * - milestone: 按里程碑分组
 * - assignee: 按负责人分组
 * - developerGroup: 按开发组分组
 */
export type GroupByOption = 'none' | 'milestone' | 'assignee' | 'developerGroup'

/**
 * 拖拽边缘类型
 * - left: 左边缘（调整开始日期）
 * - right: 右边缘（调整结束日期）
 * - body: 整体拖拽（同时调整开始和结束日期）
 */
export type DragEdge = 'left' | 'right' | 'body'

// ==================== 数据模型 ====================

/**
 * 甘特图任务
 * 从 Issue 转换而来，包含甘特图展示所需的字段
 */
export interface GanttTask {
  /** 任务 ID */
  id: string
  /** 任务编号 */
  code: string
  /** 任务标题 */
  title: string
  /** 任务类型 */
  type: IssueType
  /** 开始日期 (ISO 格式 YYYY-MM-DD) */
  startDate: string
  /** 结束日期 (ISO 格式 YYYY-MM-DD) */
  endDate: string
  /** 任务状态 */
  status: IssueStatus
  /** 优先级 */
  priority: IssuePriority
  /** 负责人 ID */
  assigneeId: string | null
  /** 负责人姓名 */
  assigneeName: string | null
  /** 里程碑 ID */
  milestoneId: string | null
  /** 里程碑名称 */
  milestoneName: string | null
  /** 父任务 ID */
  parentId: string | null
  /** 开发组 */
  developerGroup: DeveloperGroup | null
  /** 预估工时 (小时) */
  estimatedHours: number | null
  /** 进度 (0-100) */
  progress: number
  /** 是否逾期 */
  isOverdue: boolean
  /** 开始日期是否为计算值（非数据库存储） */
  isStartDateCalculated?: boolean
  /** 结束日期是否为计算值（非数据库存储） */
  isEndDateCalculated?: boolean
  /** 当前工作流阶段 */
  currentStage?: WorkflowStage
  /** 是否被回退 */
  isRolledBack?: boolean
  /** 阶段时间段 */
  stageTimeRanges?: StageTimeRange[]
}

/**
 * 甘特图里程碑
 */
export interface GanttMilestone {
  /** 里程碑 ID */
  id: string
  /** 里程碑名称 */
  title: string
  /** 截止日期 (ISO 格式 YYYY-MM-DD) */
  dueDate: string
  /** 状态 */
  status: MilestoneStatus
  /** 是否逾期 */
  isOverdue: boolean
  /** 进度 (0-100) */
  progress: number
}

/**
 * 日期范围
 */
export interface DateRange {
  /** 开始日期 */
  start: Date
  /** 结束日期 */
  end: Date
}

/**
 * 任务日期（用于拖拽更新）
 */
export interface TaskDates {
  /** 开始日期 (ISO 格式 YYYY-MM-DD) */
  startDate: string
  /** 结束日期 (ISO 格式 YYYY-MM-DD) */
  endDate: string
}

/**
 * 任务位置（用于渲染）
 */
export interface TaskPosition {
  /** 左侧偏移量 (px) */
  left: number
  /** 宽度 (px) */
  width: number
}

// ==================== 筛选和分组 ====================

/**
 * 甘特图筛选条件
 */
export interface GanttFilters {
  /** 负责人 ID */
  assigneeId?: string
  /** 状态列表 */
  status?: IssueStatus[]
  /** 优先级列表 */
  priority?: IssuePriority[]
  /** 开发组 */
  developerGroup?: DeveloperGroup
  /** 里程碑 ID */
  milestoneId?: string
  /** 任务类型 */
  type?: IssueType[]
  /** 工作流阶段 */
  workflowStage?: WorkflowStage
  /** 是否超时 */
  isOverdue?: boolean
  /** 是否被回退 */
  isRolledBack?: boolean
}

/**
 * 任务分组
 */
export interface TaskGroup {
  /** 分组 ID */
  id: string
  /** 分组名称 */
  name: string
  /** 分组内的任务 */
  tasks: GanttTask[]
  /** 是否展开 */
  isExpanded: boolean
}

// ==================== 组件 Props ====================

/**
 * 甘特图页面 Props
 */
export interface GanttChartPageProps {
  /** 项目 ID */
  projectId: string
}

/**
 * 甘特图工具栏 Props
 */
export interface GanttToolbarProps {
  /** 当前缩放级别 */
  zoomLevel: ZoomLevel
  /** 缩放级别变更回调 */
  onZoomChange: (level: ZoomLevel) => void
  /** 当前筛选条件 */
  filters: GanttFilters
  /** 筛选条件变更回调 */
  onFiltersChange: (filters: GanttFilters) => void
  /** 分组方式 */
  groupBy: GroupByOption
  /** 分组方式变更回调 */
  onGroupByChange: (groupBy: GroupByOption) => void
  /** 今日按钮点击回调 */
  onTodayClick: () => void
  /** 导航回调 */
  onNavigate: (direction: 'prev' | 'next') => void
  /** 显示依赖线 */
  showDependencies: boolean
  /** 依赖线显示切换回调 */
  onToggleDependencies: () => void
  /** 可见任务数 */
  visibleCount: number
  /** 总任务数 */
  totalCount: number
  /** 是否显示任务列表 */
  showTaskList: boolean
  /** 任务列表显示切换回调 */
  onToggleTaskList: () => void
}

/**
 * 任务列表 Props
 */
export interface TaskListProps {
  /** 任务列表或分组列表 */
  tasks: GanttTask[]
  /** 分组数据 */
  groups: TaskGroup[]
  /** 分组方式 */
  groupBy: GroupByOption
  /** 任务点击回调 */
  onTaskClick: (taskId: string) => void
  /** 分组展开/折叠回调 */
  onToggleGroup: (groupId: string) => void
  /** 行高 */
  rowHeight: number
}

/**
 * 甘特图时间线 Props
 */
export interface GanttTimelineProps {
  /** 任务列表 */
  tasks: GanttTask[]
  /** 分组数据 */
  groups: TaskGroup[]
  /** 分组方式 */
  groupBy: GroupByOption
  /** 里程碑列表 */
  milestones: GanttMilestone[]
  /** 缩放级别 */
  zoomLevel: ZoomLevel
  /** 日期范围 */
  dateRange: DateRange
  /** 任务拖拽回调 */
  onTaskDrag: (taskId: string, newDates: TaskDates) => void
  /** 任务点击回调 */
  onTaskClick: (taskId: string) => void
  /** 里程碑点击回调 */
  onMilestoneClick: (milestoneId: string) => void
  /** 是否显示依赖线 */
  showDependencies: boolean
  /** 行高 */
  rowHeight: number
  /** 滚动位置同步 */
  scrollTop: number
  /** 滚动回调 */
  onScroll: (scrollTop: number, scrollLeft: number) => void
}

/**
 * 任务条 Props
 */
export interface TaskBarProps {
  /** 任务数据 */
  task: GanttTask
  /** 缩放级别 */
  zoomLevel: ZoomLevel
  /** 日期范围 */
  dateRange: DateRange
  /** 拖拽开始回调 */
  onDragStart: (edge: DragEdge) => void
  /** 拖拽中回调 */
  onDrag: (deltaX: number) => void
  /** 拖拽结束回调 */
  onDragEnd: () => void
  /** 点击回调 */
  onClick: () => void
  /** 是否正在拖拽 */
  isDragging: boolean
  /** 行高 */
  rowHeight: number
}

/**
 * 里程碑标记 Props
 */
export interface MilestoneMarkerProps {
  /** 里程碑数据 */
  milestone: GanttMilestone
  /** 水平位置 (px) */
  position: number
  /** 点击回调 */
  onClick: () => void
}

/**
 * 依赖线 Props
 */
export interface DependencyLineProps {
  /** 起点坐标 */
  from: { x: number; y: number }
  /** 终点坐标 */
  to: { x: number; y: number }
  /** 是否高亮 */
  isHighlighted: boolean
}

// ==================== Hook 返回类型 ====================

/**
 * useGanttData Hook 返回类型
 */
export interface UseGanttDataReturn {
  /** 任务列表 */
  tasks: GanttTask[]
  /** 里程碑列表 */
  milestones: GanttMilestone[]
  /** 分组数据 */
  groups: TaskGroup[]
  /** 是否加载中 */
  isLoading: boolean
  /** 错误信息 */
  error: Error | null
  /** 可见任务数 */
  visibleCount: number
  /** 总任务数 */
  totalCount: number
}

/**
 * 日期范围预设类型
 */
export type DateRangePreset = 'week' | 'month' | 'quarter' | 'year' | 'auto' | 'custom'

/**
 * 日期范围预设配置
 */
export const DATE_RANGE_PRESETS: Record<
  Exclude<DateRangePreset, 'auto' | 'custom'>,
  { label: string; days: number }
> = {
  week: { label: '本周', days: 7 },
  month: { label: '本月', days: 30 },
  quarter: { label: '本季度', days: 90 },
  year: { label: '本年', days: 365 },
}

/**
 * useGanttState Hook 返回类型
 */
export interface UseGanttStateReturn {
  /** 缩放级别 */
  zoomLevel: ZoomLevel
  /** 设置缩放级别 */
  setZoomLevel: (level: ZoomLevel) => void
  /** 日期范围 */
  dateRange: DateRange
  /** 设置日期范围 */
  setDateRange: (range: DateRange) => void
  /** 滚动到指定日期 */
  scrollToDate: (date: Date) => void
  /** 滚动到今天 */
  scrollToToday: () => void
  /** 导航（前进/后退） */
  navigate: (direction: 'prev' | 'next') => void
  /** 筛选条件 */
  filters: GanttFilters
  /** 设置筛选条件 */
  setFilters: (filters: GanttFilters) => void
  /** 分组方式 */
  groupBy: GroupByOption
  /** 设置分组方式 */
  setGroupBy: (groupBy: GroupByOption) => void
  /** 是否显示依赖线 */
  showDependencies: boolean
  /** 切换依赖线显示 */
  toggleDependencies: () => void
  /** 展开的分组 */
  expandedGroups: Set<string>
  /** 切换分组展开状态 */
  toggleGroup: (groupId: string) => void
  /** 日期范围预设 */
  dateRangePreset: DateRangePreset
  /** 设置日期范围预设 */
  setDateRangePreset: (preset: DateRangePreset) => void
  /** 自动适配到任务范围 */
  autoFit: (tasks: GanttTask[]) => void
  /** 是否有任务在视图外 */
  hasTasksOutsideView: (tasks: GanttTask[]) => boolean
}

/**
 * useTaskDrag Hook 返回类型
 */
export interface UseTaskDragReturn {
  /** 是否正在拖拽 */
  isDragging: boolean
  /** 正在拖拽的任务 ID */
  draggedTaskId: string | null
  /** 拖拽边缘 */
  dragEdge: DragEdge | null
  /** 拖拽开始 */
  handleDragStart: (taskId: string, edge: DragEdge, initialX: number) => void
  /** 拖拽中 */
  handleDrag: (currentX: number) => void
  /** 拖拽结束 */
  handleDragEnd: () => void
  /** 预览日期（拖拽时显示） */
  previewDates: TaskDates | null
}

// ==================== 常量 ====================

/**
 * 缩放级别配置
 */
export const ZOOM_LEVEL_CONFIG: Record<ZoomLevel, {
  /** 每天的宽度 (px) */
  dayWidth: number
  /** 显示格式 */
  format: string
  /** 标签 */
  label: string
}> = {
  day: { dayWidth: 40, format: 'MM-dd', label: '日' },
  week: { dayWidth: 20, format: 'wo 周', label: '周' },
  month: { dayWidth: 8, format: 'yyyy-MM', label: '月' },
}

/**
 * 默认行高
 */
export const DEFAULT_ROW_HEIGHT = 40

/**
 * 任务列表宽度
 */
export const TASK_LIST_WIDTH = 280

/**
 * 优先级颜色映射
 */
export const PRIORITY_COLORS: Record<IssuePriority, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500',
}

/**
 * 状态进度映射
 */
export const STATUS_PROGRESS: Record<IssueStatus, number> = {
  backlog: 0,
  todo: 10,
  in_progress: 40,
  code_review: 70,
  testing: 85,
  done: 100,
  blocked: 30,
  cancelled: 0,
}

/**
 * 工作流阶段标签
 */
export const WORKFLOW_STAGE_LABELS: Record<WorkflowStage, string> = {
  created: '已创建',
  product_discussion: '产品讨论',
  ui_design: 'UI 设计',
  ui_confirmation: 'UI 确认',
  frontend_development: '前端开发',
  backend_development: '后端开发',
  code_review: '代码审查',
  ops_deployment: '运维部署',
  testing: '测试',
  product_acceptance: '产品验收',
  done: '已完成',
}

/**
 * 工作流阶段颜色
 */
export const WORKFLOW_STAGE_COLORS: Record<WorkflowStage, string> = {
  created: 'bg-gray-400',
  product_discussion: 'bg-purple-400',
  ui_design: 'bg-pink-400',
  ui_confirmation: 'bg-pink-500',
  frontend_development: 'bg-blue-400',
  backend_development: 'bg-green-400',
  code_review: 'bg-yellow-400',
  ops_deployment: 'bg-cyan-400',
  testing: 'bg-orange-400',
  product_acceptance: 'bg-indigo-400',
  done: 'bg-emerald-500',
}
